import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface CoachUser {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: string;
  approval_status: string;
  phone?: string;
}

// signIn can return either a Supabase AuthError or a custom shape for
// coach-specific failures (account not found, fetch error, unexpected errors).
type CoachSignInError =
  | AuthError
  | { message: string; code: string; originalError?: unknown };

interface CoachAuthContextType {
  user: User | null;
  session: Session | null;
  coachUser: CoachUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: CoachSignInError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  isCoach: boolean;
}

const CoachAuthContext = createContext<CoachAuthContextType | undefined>(undefined);

export function CoachAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [coachUser, setCoachUser] = useState<CoachUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCoachUser = async (userId: string) => {
    try {
      console.log('🔧 Fetching coach user for userId:', userId);
      
      // Try using the security definer function first
      const { data: functionData, error: functionError } = await supabase
        .rpc('get_current_coach');
      
      if (functionError) {
        console.log('🔧 Function method failed, trying direct query:', functionError);
        
        // Fallback to direct query
        const { data, error } = await supabase
          .from('coaches')
          .select('*')
          .eq('user_id', userId)
          .eq('approval_status', 'approved')
          .maybeSingle();

        if (error) {
          console.error('Error fetching coach user:', error);
          return null;
        }

        if (!data) {
          console.log('No coach found for user_id:', userId);
          return null;
        }

        console.log('✅ Coach data found via direct query:', data);
        return data;
      } else {
        // Function worked, use the first result
        const coachData = Array.isArray(functionData) ? functionData[0] : functionData;
        if (!coachData) {
          console.log('No coach found via function for user_id:', userId);
          return null;
        }
        
        console.log('✅ Coach data found via function:', coachData);
        return coachData;
      }
    } catch (error) {
      console.error('Error in fetchCoachUser:', error);
      return null;
    }
  };

  useEffect(() => {
    console.log('🔧 CoachAuth: Setting up auth listener');
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔧 CoachAuth: Auth state changed', { event, hasSession: !!session, userId: session?.user?.id });
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user?.id) {
          console.log('🔧 CoachAuth: User logged in, fetching coach data for:', session.user.id);
          // Defer async operations to prevent auth state deadlocks
          setTimeout(async () => {
            try {
              const coachData = await fetchCoachUser(session.user.id);
              console.log('🔧 CoachAuth: Coach data fetched:', coachData);
              setCoachUser(coachData);
              setLoading(false);
            } catch (error) {
              console.error('🔧 CoachAuth: Error fetching coach data:', error);
              setCoachUser(null);
              setLoading(false);
            }
          }, 0);
        } else {
          console.log('🔧 CoachAuth: No user, clearing coach data');
          setCoachUser(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    console.log('🔧 CoachAuth: Checking for existing session');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔧 CoachAuth: Initial session check', { hasSession: !!session, userId: session?.user?.id });
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user?.id) {
        console.log('🔧 CoachAuth: Initial session found, fetching coach data for:', session.user.id);
        // Use setTimeout to defer async operations here too
        setTimeout(async () => {
          try {
            const coachData = await fetchCoachUser(session.user.id);
            console.log('🔧 CoachAuth: Initial coach data fetched:', coachData);
            setCoachUser(coachData);
            setLoading(false);
          } catch (error) {
            console.error('🔧 CoachAuth: Error fetching initial coach data:', error);
            setCoachUser(null);
            setLoading(false);
          }
        }, 0);
      } else {
        console.log('🔧 CoachAuth: No initial session found');
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('🔧 useCoachAuth: Attempting signIn for:', email);
    
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      console.log('🔧 useCoachAuth: Auth signIn result:', { 
        error, 
        hasUser: !!data?.user,
        userId: data?.user?.id 
      });
      
      if (error) {
        console.error('🔧 useCoachAuth: Auth signIn error:', error);
        return { error };
      }
      
      // If auth succeeds, wait a moment for the auth state to propagate
      // and then try to fetch coach data manually to provide better error handling
      if (data?.user) {
        console.log('🔧 useCoachAuth: Auth successful, checking coach data for:', data.user.id);
        
        try {
          // Give a small delay for auth state to propagate
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const coachData = await fetchCoachUser(data.user.id);
          console.log('🔧 useCoachAuth: Coach data check result:', coachData);
          
          if (!coachData) {
            console.error('🔧 useCoachAuth: No coach data found for authenticated user');
            return { 
              error: { 
                message: "Coach account not found or not approved. Please contact your administrator.",
                code: "COACH_NOT_FOUND"
              }
            };
          }
        } catch (fetchError) {
          console.error('🔧 useCoachAuth: Error fetching coach data after auth:', fetchError);
          return { 
            error: { 
              message: "Error verifying coach account. Please try again or contact support.",
              code: "COACH_FETCH_ERROR",
              originalError: fetchError
            }
          };
        }
      }
      
      return { error: null };
    } catch (unexpectedError) {
      console.error('🔧 useCoachAuth: Unexpected signIn error:', unexpectedError);
      return { 
        error: { 
          message: `Authentication error: ${unexpectedError instanceof Error ? unexpectedError.message : 'Unknown error'}`,
          code: "UNEXPECTED_ERROR"
        }
      };
    }
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/coach-portal`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const isCoach = !!coachUser && coachUser.approval_status === 'approved';

  const value = {
    user,
    session,
    coachUser,
    loading,
    signIn,
    signUp,
    signOut,
    isCoach,
  };

  return (
    <CoachAuthContext.Provider value={value}>
      {children}
    </CoachAuthContext.Provider>
  );
}

export function useCoachAuth() {
  const context = useContext(CoachAuthContext);
  if (context === undefined) {
    throw new Error('useCoachAuth must be used within a CoachAuthProvider');
  }
  return context;
}